import pluginSource from './_plugin/zaimanhua.js?raw';
import { loadVeneraPlugin } from '../_venera/load.ts';
import type {
  ComicCard,
  ComicDetails,
  ComicSource,
  ListOpts,
  Page,
  PageRef,
  PopularOpts,
  SearchQuery,
  SourceAuth,
  SourceCommentOpts,
  SourceCommentPage,
} from '../types.ts';
import type { VeneraComicSource } from '../_venera/types.ts';

const SOURCE_ID = 'zaimanhua' as const;

const capabilities = {
  login: true,
  favorites: true,
  multiFavCategories: false,
  search: true,
  advancedSearch: false,
  comments: true,
  loginNotes:
    'Zai Manga receives your username and an MD5-hashed password via Bunko. Bunko stores only the source bearer token returned by Zai Manga.',
} satisfies ComicSource['capabilities'];

let loadedSource: Promise<VeneraComicSource> | undefined;

function loadSource(): Promise<VeneraComicSource> {
  loadedSource ??= loadVeneraPlugin({
    sourceId: SOURCE_ID,
    jsSource: `${pluginSource}\nnew Zaimanhua();`,
    settings: { signTask: false },
    capabilities,
    defaultExploreKey: '再漫画 更新',
  });
  return loadedSource;
}

export const source: ComicSource = {
  id: SOURCE_ID,
  name: '再漫画',
  description: 'Zai Manga via the pinned Venera plugin.',
  capabilities,

  async search(query: SearchQuery, auth?: SourceAuth): Promise<Page<ComicCard>> {
    return (await loadSource()).search(query, auth);
  },

  async getPopular(opts?: PopularOpts, auth?: SourceAuth): Promise<Page<ComicCard>> {
    return (await loadSource()).getPopular(opts, auth);
  },

  async getComic(id: string, auth?: SourceAuth): Promise<ComicDetails> {
    return (await loadSource()).getComic(id, auth);
  },

  async getPages(id: string, auth?: SourceAuth): Promise<PageRef[]> {
    return (await loadSource()).getPages(id, auth);
  },

  async getComments(id: string, auth?: SourceAuth, opts?: SourceCommentOpts): Promise<SourceCommentPage> {
    return (await loadSource()).getComments!(id, auth, opts);
  },

  async resolveImageUrl(ref: PageRef, auth?: SourceAuth): Promise<{ url: string }> {
    return (await loadSource()).resolveImageUrl(ref, auth);
  },

  async validateAuth(auth: SourceAuth): Promise<{ ok: boolean; username?: string; reason?: string }> {
    return (await loadSource()).validateAuth?.(auth) ?? { ok: false, reason: 'Zai Manga auth is unavailable' };
  },

  async loginWithForm(request: { username: string; password: string }) {
    return (await loadSource()).loginWithForm?.(request) ?? { ok: false, reason: 'Zai Manga login is unavailable' };
  },

  async listFavorites(auth: SourceAuth, opts: ListOpts): Promise<Page<ComicCard>> {
    const source = await loadSource();
    if (!source.listFavorites) return { items: [], page: opts.page ?? 1, hasMore: false };
    return source.listFavorites(auth, opts);
  },

  async addFavorite(id: string, auth: SourceAuth, category?: number): Promise<void> {
    await (await loadSource()).addFavorite?.(id, auth, category);
  },

  async removeFavorite(id: string, auth: SourceAuth): Promise<void> {
    await (await loadSource()).removeFavorite?.(id, auth);
  },
};

export default source;
